import React, { useEffect, useMemo } from 'react'
import { Modal, Button, Form, Input, Select, Upload, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { getSolicitantAreasOptions } from 'services/demands/demandsServices'
import { useQuery } from '@tanstack/react-query'

const { TextArea } = Input

const DemandsModal = ({
	show,
	onCancel,
	onConfirm,
	type, // 'approve' | 'previousStep' | 'archive'
	loading = false,
	workflowData,
}) => {
	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	const optionalRouteOptions = [
		{ value: 'nenhuma', label: 'Nenhuma' },
		{ value: 'area_tecnica', label: 'Área técnica' },
		{ value: 'diretoria', label: 'Apenas diretoria' },
		{ value: 'area_tecnica_diretoria', label: 'Diretoria e Área Técnica' },
	]

	const { data: solicitantAreas, isLoading: isLoadingAreas } = useQuery({
		queryKey: ['solicitant-areas-options'],
		queryFn: getAreas,
	})

	async function getAreas() {
		const areas = await getSolicitantAreasOptions('')

		return areas?.filter((area) => area.managementTeamId)
	}

	const titleDict = {
		approve: 'Aprovar',
		previousStep: 'Devolver para ajustes',
		archive: 'Arquivar',
		advance: 'Avançar Etapa',
		return: 'Devolver para ajustes',
	}

	const title = titleDict[type]

	const areaDict = {
		1: 'Área Solicitante',
		2: 'Área Gestora',
		3: 'Área Técnica',
		4: 'Diretoria',
		5: 'Área Jurídica',
		6: 'Área de confirmação do solicitante',
	}

	const previousSteps = useMemo(() => {
		const currentStep = workflowData?.task?.current_workflow_step
		if (!currentStep) return []

		const route =
			values?.optionalRoute || workflowData?.task?.workflow_meta?.route
		let stepsToReturn = []

		for (let i = 1; i < currentStep; i++) {
			if (i === 3 && ['diretoria', 'nenhuma'].includes(route)) continue
			if (i === 4 && ['area_tecnica', 'nenhuma'].includes(route)) continue
			stepsToReturn.push({
				value: i,
				label: areaDict[i] || `Etapa ${i}`,
			})
		}
		return stepsToReturn
	}, [workflowData, type, values?.optionalRoute])

	useEffect(() => {
		if (show) {
			form.resetFields()
		}
	}, [show, form])

	useEffect(() => {
		const currentReturnArea = form.getFieldValue('returnArea')
		if (currentReturnArea !== undefined) {
			const validValues = previousSteps.map((s) => s.value)
			if (!validValues.includes(currentReturnArea)) {
				form.setFieldValue('returnArea', undefined)
			}
		}
	}, [previousSteps])

	const handleCancel = () => {
		form.resetFields()
		onCancel()
	}

	const handleConfirm = () => {
		form.validateFields().then((formValues) => {
			onConfirm(formValues)
		})
	}

	const isFormValid = useMemo(() => {
		if (type === 'archive') {
			return !!values?.justification
		}
		if (type === 'approve') {
			if (
				workflowData?.task?.current_workflow_step === 2 &&
				!workflowData?.task?.workflow_meta?.route
			) {
				return !!values?.justification && !!values?.optionalRoute
			} else {
				return !!values?.justification
			}
		}
		if (type === 'previousStep') {
			return !!values?.justification && !!values?.returnArea
		}
		return false
	}, [values, type])

	const footerButtons = [
		<Button key="cancel" onClick={handleCancel}>
			Cancelar
		</Button>,
		<Button
			key="confirm"
			type="primary"
			onClick={handleConfirm}
			loading={loading}
			disabled={!isFormValid || loading}>
			Confirmar
		</Button>,
	]

	function renderFields() {
		switch (type) {
			case 'approve':
				return (
					<>
						<Form.Item
							name="justification"
							label="Justificativa"
							rules={[
								{
									required: true,
									message: 'Por favor, insira uma justificativa',
								},
							]}>
							<TextArea
								rows={4}
								placeholder="Digite aqui a justificativa para esta ação..."
							/>
						</Form.Item>

						{!workflowData?.task?.workflow_meta?.route &&
							workflowData?.task?.current_workflow_step === 2 && (
								<>
									<Form.Item
										name="optionalRoute"
										label="Rota opcional"
										rules={[
											{
												required: true,
												message: 'Por favor, selecione uma rota',
											},
										]}>
										<Select placeholder="Selecione a rota opcional">
											{optionalRouteOptions.map((option) => (
												<Select.Option key={option.value} value={option.value}>
													{option.label}
												</Select.Option>
											))}
										</Select>
									</Form.Item>
									{['area_tecnica', 'area_tecnica_diretoria'].includes(
										values?.optionalRoute
									) && (
										<Form.Item
											name="technicalAreaId"
											label="Área Técnica"
											rules={[
												{
													required: true,
													message: 'Por favor, selecione uma área técnica',
												},
											]}>
											<Select
												placeholder="Selecione a área técnica"
												notFoundContent={
													isLoadingAreas ? (
														<Spin size="small" />
													) : (
														'Nenhuma área técnica encontrada'
													)
												}>
												{solicitantAreas?.map((option) => (
													<Select.Option
														key={option.value}
														value={option.value}>
														{option.label}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									)}
								</>
							)}

						<Form.Item
							name="files"
							label="Anexo (opcional)"
							valuePropName="fileList"
							getValueFromEvent={(e) => {
								if (Array.isArray(e)) return e
								return e && e.fileList
							}}
							tooltip="Arquivos anexados aqui constarão no controle de versões da demanda, e poderão ser acessados em futuras etapas do fluxo.">
							<Upload beforeUpload={() => false} multiple maxCount={5}>
								<Button icon={<UploadOutlined />}>Selecionar arquivos</Button>
							</Upload>
						</Form.Item>
					</>
				)
			case 'previousStep':
				return (
					<>
						<Form.Item
							name="justification"
							label="Justificativa"
							rules={[
								{
									required: true,
									message: 'Por favor, insira uma justificativa',
								},
							]}>
							<TextArea
								rows={4}
								placeholder="Digite aqui a justificativa para esta ação..."
							/>
						</Form.Item>
						<Form.Item
							name="returnArea"
							label="Etapa para qual deve ser retornada"
							rules={[
								{
									required: true,
									message: 'Para qual etapa essa demanda deve retornar?',
								},
							]}>
							<Select placeholder="Selecione uma opção">
								{previousSteps?.map((step) => (
									<Select.Option value={step.value}>{step.label}</Select.Option>
								))}
							</Select>
						</Form.Item>
						{workflowData?.task?.current_workflow_step === 5 && (
							<Form.Item name="optionalRoute" label="Rota opcional">
								<Select placeholder="Selecione a rota opcional">
									{optionalRouteOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						)}
						<Form.Item
							name="files"
							label="Anexo (opcional)"
							valuePropName="fileList"
							getValueFromEvent={(e) => {
								if (Array.isArray(e)) return e
								return e && e.fileList
							}}
							tooltip="Arquivos anexados aqui constarão no controle de versões da demanda, e poderão ser acessados em futuras etapas do fluxo.">
							<Upload beforeUpload={() => false} multiple maxCount={5}>
								<Button icon={<UploadOutlined />}>Selecionar arquivos</Button>
							</Upload>
						</Form.Item>
					</>
				)
			case 'archive':
				return (
					<>
						<Form.Item
							name="justification"
							label="Justificativa"
							rules={[
								{
									required: true,
									message: 'Por favor, insira uma justificativa',
								},
							]}>
							<TextArea
								rows={4}
								placeholder="Digite aqui a justificativa para esta ação..."
							/>
						</Form.Item>
					</>
				)
			default:
				return null
		}
	}

	return (
		<Modal
			open={show}
			title={title}
			onCancel={handleCancel}
			footer={footerButtons}
			destroyOnClose>
			<Form
				form={form}
				layout="vertical"
				initialValues={{ technicalEvaluation: false }}>
				{renderFields()}
			</Form>
		</Modal>
	)
}

DemandsModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	type: PropTypes.oneOf(['approve', 'previousStep', 'advance', 'return'])
		.isRequired,
	loading: PropTypes.bool,
	areas: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		})
	),
}

export default DemandsModal
